import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import AdSlot from "@/components/AdSlot";
import {
  Waves, Flame, Droplets, Cpu, Sparkles, ShieldCheck,
  ArrowRight, Clock, CheckCircle2, Zap,
} from "lucide-react";

const CATEGORIES = [
  { icon: Droplets, label: "Pompe & Filtration", accent: "bg-blue-100 text-blue-700" },
  { icon: Flame, label: "Chauffage", accent: "bg-orange-100 text-orange-700" },
  { icon: Sparkles, label: "Qualité de l'eau", accent: "bg-emerald-100 text-emerald-700" },
  { icon: Cpu, label: "Électronique", accent: "bg-violet-100 text-violet-700" },
];

const STEPS = [
  { n: "01", title: "Répondez à l'arbre de diagnostic", desc: "Un questionnaire branché en 6 axes techniques adapté à vos symptômes. Aucune compétence requise." },
  { n: "02", title: "Obtenez votre score santé", desc: "Un score par catégorie et une vue d'ensemble instantanée de l'état de votre spa." },
  { n: "03", title: "Débloquez le rapport détaillé", desc: "Causes probables, actions concrètes et coûts estimés pour chaque problème identifié." },
];

const PARTNERS_URL = "https://spa-diagnostic.preview.emergentagent.com/api/partners/";
const PARTNERS_CACHE_KEY = "doctospa_partners_cache";
const PARTNERS_CACHE_TTL = 5 * 60 * 1000;

const LOGO_MAP = {
  spanet: "/partners/spanet.svg",
  espa: "/partners/espa.svg",
  lx: "/partners/lx.png",
};

const PARTNERS_FALLBACK = [
  { id: "balboa", name: "Balboa", sub: "Systèmes de contrôle & packs" },
  { id: "gecko", name: "Gecko", sub: "Packs électroniques & claviers" },
  { id: "waterway", name: "Waterway", sub: "Pompes & hydraulique" },
  { id: "lx", name: "LX Whirlpool", sub: "Pompes de massage" },
];

export default function Landing() {
  const [partners, setPartners] = useState(PARTNERS_FALLBACK);

  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(PARTNERS_CACHE_KEY) || "null");
      if (cached && Date.now() - cached.ts < PARTNERS_CACHE_TTL && cached.items?.length) {
        setPartners(cached.items);
        return;
      }
    } catch {
      /* cache illisible → on refetch */
    }
    fetch(PARTNERS_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("bad status"))))
      .then((data) => {
        const items = (data.items || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        if (items.length) {
          setPartners(items);
          try {
            localStorage.setItem(PARTNERS_CACHE_KEY, JSON.stringify({ ts: Date.now(), items }));
          } catch {
            /* stockage plein — tant pis pour le cache */
          }
        }
      })
      .catch(() => {
        /* erreur réseau → fallback hardcodé déjà affiché */
      });
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-black/10 bg-white text-xs font-semibold" data-testid="hero-badge">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                Diagnostic 100% gratuit — 2 min chrono
              </div>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                Votre spa fait des<br />siennes ?<br />
                <span className="text-primary">Diagnostiquez-le</span><br />
                en 2 minutes.
              </h1>
              <p className="mt-6 text-base lg:text-lg text-muted-foreground max-w-xl leading-relaxed">
                DoctoSpa analyse les symptômes de votre spa/jacuzzi et identifie les
                causes probables — bien avant qu'un technicien ne facture le déplacement.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/diagnostic">
                  <Button size="lg" className="rounded-full h-12 px-7 text-sm font-semibold" data-testid="hero-cta-start">
                    Commencer mon diagnostic <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline" className="rounded-full h-12 px-6 text-sm font-semibold" data-testid="hero-cta-pricing">
                    Voir les tarifs
                  </Button>
                </Link>
              </div>
              <div className="mt-10 flex items-center gap-6 text-xs text-muted-foreground">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Sans engagement</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Résultat instantané</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> RGPD-compatible</div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-primary/5 rounded-3xl blur-2xl" />
                <Card className="relative rounded-2xl border-black/10 overflow-hidden shadow-[0_20px_60px_rgba(15,23,42,0.12)]">
                  <div className="aspect-[4/5] relative" data-testid="hero-image">
                    <img
                      src="/hero/hero-main.jpg"
                      alt="Spa jacuzzi installé en extérieur — réalisation DoctoSpa"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <div className="eyebrow text-white/70 mb-2">Aperçu diagnostic</div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-display text-5xl font-black">72</span>
                        <span className="text-sm text-white/80">/100</span>
                      </div>
                      <div className="mt-2 text-sm text-amber-300 font-semibold flex items-center gap-1.5">
                        <Zap className="h-3.5 w-3.5" /> 2 points d'attention
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 border-t border-black/10">
                    {CATEGORIES.map((c) => (
                      <div key={c.label} className="p-3 flex flex-col items-center border-r last:border-r-0 border-black/5">
                        <span className={`h-8 w-8 rounded-md grid place-items-center ${c.accent}`}>
                          <c.icon className="h-4 w-4" />
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
              <div className="mt-4 text-center">
                <Link to="/installations" className="text-sm font-semibold text-primary hover:underline" data-testid="hero-installations-link">
                  Découvrez nos installations →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AD SLOT — home banner between hero and categories */}
      <div className="mx-auto max-w-7xl px-6 lg:px-10 -mt-4 md:-mt-2 mb-4">
        <AdSlot placement="home" />
      </div>

      {/* PARTENAIRES TECHNIQUES */}
      <section className="py-12 border-t border-black/5 bg-stone-50" data-testid="partners-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <p className="text-center text-[11px] font-bold uppercase tracking-[0.25em] text-muted-foreground">
            Partenaires techniques — pièces d'origine
          </p>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {partners.map((p) => {
              const logoSrc = LOGO_MAP[p.id] || p.logo_url;
              const tile = (
                <>
                  {logoSrc ? (
                    <img src={logoSrc} alt={p.name} className="h-9 mx-auto object-contain" loading="lazy" />
                  ) : (
                    <span className="flex items-center justify-center gap-2.5">
                      {p.domain && (
                        <img
                          src={`https://unavatar.io/${p.domain}?fallback=false`}
                          alt=""
                          className="h-8 w-8 rounded-lg object-contain"
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      )}
                      <span
                        className="font-display text-xl lg:text-2xl font-black tracking-tight text-slate-800 group-hover:text-primary transition-colors"
                        style={p.color ? { color: p.color } : undefined}
                      >
                        {p.name}
                      </span>
                    </span>
                  )}
                  <p className="mt-1 text-[11px] text-muted-foreground">{p.sub}</p>
                </>
              );
              const cls =
                "group block rounded-xl border border-black/10 bg-white px-6 py-5 text-center transition-transform duration-200 hover:-translate-y-1 hover:border-primary/40";
              const tid = `partner-${(p.id || p.name).toLowerCase().replace(/\s+/g, "-")}`;
              return p.website ? (
                <a key={p.id || p.name} href={p.website} target="_blank" rel="noopener noreferrer" className={cls} data-testid={tid}>
                  {tile}
                </a>
              ) : (
                <div key={p.id || p.name} className={cls} data-testid={tid}>
                  {tile}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-20 border-t border-black/5 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="eyebrow">Ce que nous diagnostiquons</div>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold tracking-tight">
              4 catégories techniques couvrent 95% des pannes de spa.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CATEGORIES.map((c) => (
              <div key={c.label} className="p-6 border border-black/10 rounded-xl hover:border-primary/40 hover:-translate-y-1 transition-transform duration-200 bg-white" data-testid={`cat-card-${c.label}`}>
                <span className={`h-10 w-10 rounded-lg grid place-items-center ${c.accent}`}>
                  <c.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold">{c.label}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {c.label === "Pompe & Filtration" && "Débit, bruits anormaux, colmatage du filtre."}
                  {c.label === "Chauffage" && "Résistance, sonde, codes d'erreur FLO/OH/HL."}
                  {c.label === "Qualité de l'eau" && "pH, couleur, odeur, désinfection."}
                  {c.label === "Électronique" && "Panneau, disjoncteur, éclairage LED."}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="eyebrow">Comment ça marche</div>
              <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold tracking-tight">
                Un diagnostic clair, sans jargon.
              </h2>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                Notre moteur d'analyse s'appuie sur les pannes les plus fréquentes constatées
                sur les spas domestiques. Réponses simples, résultats immédiats.
              </p>
              <Link to="/diagnostic" className="mt-8 inline-flex">
                <Button className="rounded-full h-11 px-6" data-testid="how-cta">Démarrer maintenant</Button>
              </Link>
            </div>
            <div className="lg:col-span-8 space-y-6">
              {STEPS.map((s, idx) => (
                <div key={s.n} className="grid grid-cols-12 gap-6 items-start p-6 border border-black/10 rounded-xl bg-white">
                  <div className="col-span-2">
                    <span className="font-display text-4xl font-black text-primary">{s.n}</span>
                  </div>
                  <div className="col-span-10">
                    <h3 className="font-display text-xl font-semibold">{s.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-20 border-t border-black/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Clock, title: "2 minutes", desc: "Le temps moyen d'un diagnostic complet." },
            { icon: ShieldCheck, title: "Vos données sont privées", desc: "Aucune donnée revendue, RGPD-compatible." },
            { icon: Waves, title: "Pensé pour l'usage domestique", desc: "Spas et jacuzzis résidentiels, toutes marques." },
          ].map((t) => (
            <div key={t.title} className="flex items-start gap-4">
              <span className="h-11 w-11 grid place-items-center rounded-lg bg-secondary text-secondary-foreground">
                <t.icon className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-lg font-semibold">{t.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{t.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-2xl bg-secondary text-secondary-foreground p-10 lg:p-16">
            <div className="absolute inset-0 grid-pattern-dense opacity-30 pointer-events-none" />
            <div className="relative">
              <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tighter max-w-2xl">
                Prêt à comprendre ce qui cloche avec votre spa ?
              </h2>
              <p className="mt-4 text-white/70 max-w-xl">
                Diagnostic gratuit. Résultats en 2 minutes. Rapport détaillé disponible pour 9,99€.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/diagnostic">
                  <Button size="lg" variant="secondary" className="rounded-full h-12 px-7 bg-white text-secondary hover:bg-white/90" data-testid="footer-cta-diagnostic">
                    Diagnostiquer mon spa
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline" className="rounded-full h-12 px-6 bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white" data-testid="footer-cta-pricing">
                    Voir les tarifs
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
